// sentItemIds — the client copy of the §7.3 id mapping: thread id → item id,
// and /docs `(kind, sk, sid)` → item id.
//
// Why a client copy exists at all: old URLs are in people's inboxes, in Linear
// comments and in Slack (T20). `/threads/lease:abc` has to keep working the
// moment the thread page stops existing, and a redirect cannot ask the server
// what an id maps to — the browser has to know before it navigates.
//
// PURE ON PURPOSE. No React, no `@/lib/api` anywhere in the import graph, so
// these can be tested with a bare `npx tsx --test` rather than under the
// module-mock flag (T21). `sentItemsClient.ts` re-exports the two functions the
// plan names, so callers can keep importing from the one client module.
//
// The formats are NOT invented here. They are read off the code that produces
// them and restated (the web cannot import across the workspace boundary):
//
//   • thread ids   `${type}:${nativeId}` — apps/api/src/lib/commandCenter/
//                  contracts.ts `threadId()`; `ThreadType` is the 14-member
//                  union in the same file.
//   • doc ids      the signature family is addressed by `groupId`; a rental
//                  verification by `encodeDocId(groupId, sk)` = `groupId~sk`;
//                  an RRA verification by `caseId~verificationId`
//                  (apps/api/src/lib/viewDocs/docModel.ts).
//   • item ids     §5.1 of the plan — `<kind>:<nativeId>`, with `#` joining a
//                  sub-id (a rental verification's landlord key).
//
// If any of those three change, this file is where the redirect breaks, and
// sentItemIds.test.ts is where it is caught.

import type { SentItemKind } from '@/lib/sentItemsClient';

/** The composite separator inside a /docs id (`encodeDocId`'s ID_SEP). */
const DOC_ID_SEP = '~';

/** Where a redirect that cannot resolve an item lands. */
export const SENT_ITEMS_PATH = '/sent-items';

// ---------------------------------------------------------------------------
// Thread id → item id (§7.3, reverse)
// ---------------------------------------------------------------------------

/**
 * Thread type → the row kind its item is filed under.
 *
 * Three of these are renames rather than identities, and each one is a
 * deliberate collapse the plan makes:
 *
 *   renewal → lease            a renewal is a `lease` row carrying
 *                              `isRenewal` (§5.1, T16), not a kind.
 *   moveout_funds → moveout_funds_direction
 *                              the thread is named for the workflow, the
 *                              document for what it is.
 *   owner_agreement → property_management_agreement
 *                              same: the thread spans the owner engagement,
 *                              the item is the EPMA document.
 *
 * `null` means the thread has no single item behind it, and the value says
 * why — see `THREAD_WITHOUT_ITEM`.
 */
const ITEM_KIND_BY_THREAD_TYPE: Record<string, SentItemKind> = {
  lease: 'lease',
  renewal: 'lease',
  adoption_addendum: 'adoption_addendum',
  payment_plan: 'payment_plan',
  condition_report: 'condition_report',
  moveout_funds: 'moveout_funds_direction',
  manual_document: 'manual_document',
  leaseholder_change: 'leaseholder_change',
  leaseholder_removal: 'leaseholder_removal',
  animal_addendum: 'animal_addendum',
  animal_addendum_paid: 'animal_addendum_paid',
  owner_agreement: 'property_management_agreement',
  intake: 'intake',
  // Identity, and per LANDLORD on both sides — see THREAD_WITHOUT_ITEM.
  rental_verification: 'rental_verification',
};

/**
 * Thread types that resolve to NO item, and why.
 *
 *   occupancy_sync   T13: the sync is a trailing STATE of the lease or
 *                    condition report it belongs to, not a row. The docKey it
 *                    is named for is a server-side coordinate.
 *
 * `rental_verification` is NOT in this set. Its thread id is per LANDLORD, not
 * per group — `threadId('rental_verification', `${groupId}#${rv.sk}`)`
 * (snapshot.ts §5g) — which is exactly the item id §5.1 gives it. So the two
 * are the same string and the mapping is an identity. Only the degenerate
 * `rental_verification:<groupId>` with no `#` is unresolvable, and that shape
 * is handled below rather than by excluding the type outright.
 */
const THREAD_WITHOUT_ITEM = new Set(['occupancy_sync']);

/**
 * The row kind a THREAD TYPE belongs to — the `/agreements?type=` filter's
 * translation, separate from the id mapping because a type with no id is still
 * a perfectly good filter.
 */
export function itemKindForThreadType(type: string | null | undefined): SentItemKind | null {
  if (!type) return null;
  return ITEM_KIND_BY_THREAD_TYPE[type.trim()] ?? null;
}

/**
 * Split `type:nativeId` at the FIRST colon only — a nativeId may itself contain
 * ':' and '#' (this is `parseThreadId`'s rule, restated).
 */
function splitPrefixed(id: string): { prefix: string; rest: string } | null {
  const trimmed = id.trim();
  const sep = trimmed.indexOf(':');
  if (sep <= 0 || sep === trimmed.length - 1) return null;
  return { prefix: trimmed.slice(0, sep), rest: trimmed.slice(sep + 1) };
}

/**
 * The `/items/:itemId` id for an old `/threads/:threadId`.
 *
 * Returns `null` when no single item is behind the thread — the caller sends
 * those to the list (see `redirectPathForThreadId`, which also knows the two
 * cases that deserve a better landing than a bare list).
 *
 * ONE exception to "returns an item id": `household:<caseId>` returns the PATH
 * `/households/<caseId>`. Household files are not items and never had an
 * `/items/` id; a caller distinguishes the two by the leading '/'. That is the
 * plan's instruction and it is worth the wart — the alternative is a second
 * function every caller has to remember to also call.
 */
export function itemIdForThreadId(threadId: string): string | null {
  const parsed = splitPrefixed(threadId);
  if (!parsed) return null;
  const { prefix, rest } = parsed;

  // Not a thread at all: a household file, which has its own route.
  if (prefix === 'household') return `/households/${encodeURIComponent(rest)}`;

  if (THREAD_WITHOUT_ITEM.has(prefix)) return null;

  // A rental verification is one row per landlord, and the landlord key rides
  // the thread id. Without it there is no single request to open — the caller
  // searches the group instead.
  if (prefix === 'rental_verification' && !rest.includes('#')) return null;

  const kind = ITEM_KIND_BY_THREAD_TYPE[prefix];
  if (!kind) return null; // RRA has no thread; anything else is not a thread id
  return `${kind}:${rest}`;
}

/**
 * Where an old `/threads/:threadId` should actually land.
 *
 * Everything resolvable goes to its item. A rental-verification thread is the
 * one case worth more than a bare list: the group is known even though the
 * landlord is not, so the list opens pre-searched for that group rather than
 * dropping the operator at the top of everything ever sent.
 */
export function redirectPathForThreadId(threadId: string): string {
  const resolved = itemIdForThreadId(threadId);
  if (resolved?.startsWith('/')) return resolved;
  if (resolved) return `/items/${encodeURIComponent(resolved)}`;

  // Only the group-only RV shape reaches here — one with a landlord key
  // resolved above.
  const parsed = splitPrefixed(threadId);
  if (parsed?.prefix === 'rental_verification') {
    return `${SENT_ITEMS_PATH}?q=${encodeURIComponent(parsed.rest)}`;
  }
  return SENT_ITEMS_PATH;
}

// ---------------------------------------------------------------------------
// Doc kind + doc id → item id (§5.1)
// ---------------------------------------------------------------------------

/**
 * `ViewDocKind` → the row kind it is filed under.
 *
 * The only many-to-one is intake: a household's three intake documents are ONE
 * row with three sections (Q2, §5.5). `rra_intake` is NOT one of them (T12) —
 * it has its own kind, its own gate and its own subject.
 */
const ITEM_KIND_BY_DOC_KIND: Record<string, SentItemKind> = {
  lease: 'lease',
  adoption_addendum: 'adoption_addendum',
  leaseholder_change: 'leaseholder_change',
  leaseholder_removal: 'leaseholder_removal',
  payment_plan: 'payment_plan',
  animal_addendum: 'animal_addendum',
  animal_addendum_paid: 'animal_addendum_paid',
  condition_report: 'condition_report',
  moveout_funds_direction: 'moveout_funds_direction',
  manual_document: 'manual_document',
  property_management_agreement: 'property_management_agreement',
  owner_contact_addendum: 'owner_contact_addendum',
  city_utility_agreement: 'city_utility_agreement',
  w9: 'w9',
  intake_designation: 'intake',
  intake_yard_care: 'intake',
  intake_animals: 'intake',
  rental_verification: 'rental_verification',
  owner_intake: 'owner_intake',
  ccr_upload: 'ccr_upload',
  rra_intake: 'rra_intake',
  rra_verification: 'rra_verification',
};

/** The row kind a `/docs` kind belongs to, or null if it is not a doc kind. */
export function itemKindForDocKind(docKind: string | null | undefined): SentItemKind | null {
  if (!docKind) return null;
  return ITEM_KIND_BY_DOC_KIND[docKind.trim()] ?? null;
}

/**
 * The RRA kinds, which resolve to no item id here on purpose.
 *
 * Not because the id could not be formed — it could — but because a redirect
 * is a NAVIGATION, and navigating an off-roster viewer into an accommodation
 * record so the server can 403 them teaches the wrong thing about what exists
 * (§5.7, T5). They go to the list, where the standing withheld line is the only
 * thing said about accommodations, identically for every viewer.
 */
const NO_REDIRECT_KINDS = new Set<SentItemKind>(['rra_intake', 'rra_verification']);

/**
 * The `/items/:itemId` id for an old `/docs?sk=<kind>&sid=<id>`.
 *
 * `kind` is the LIST filter (`/docs?kind=`), `sk`/`sid` address the open
 * document. The open document wins when both are present: a list filtered to
 * leases can have any one lease open, and it is the open one the link meant.
 *
 * Two id shapes have to be undone rather than passed through:
 *
 *   rental_verification   `sid` is `encodeDocId(groupId, sk)` = `groupId~sk`,
 *                         and the item is `rental_verification:<groupId>#<sk>`
 *                         (§5.1). This is the one place a client CAN resolve an
 *                         RV — the doc id carries the landlord key the thread
 *                         id does not.
 *   w9                    `sid` is the VERSION id, not a groupId, because a
 *                         W-9's groupId derives its public /sign/:uuid and must
 *                         never reach a browser (T25/T6). The item id therefore
 *                         carries the version id too. §5.1's table says
 *                         `<kind>:<groupId>` for w9, which cannot be true
 *                         alongside T25 — T25 is the rule with the TIN behind
 *                         it, so it wins. Flagged in W5-NOTES.
 */
export function itemIdForDocParams(
  kind: string,
  sk?: string | null,
  sid?: string | null,
): string | null {
  const docKind = (sk ?? '').trim() || (kind ?? '').trim();
  const itemKind = itemKindForDocKind(docKind);
  if (!itemKind) return null;
  if (NO_REDIRECT_KINDS.has(itemKind)) return null;

  const docId = (sid ?? '').trim();
  // Without the open document there is no native id to build an item id from —
  // a kind alone is a FILTER, and the caller turns it into one (`types=`).
  if (!docId) return null;

  if (itemKind === 'rental_verification') {
    const parts = docId.split(DOC_ID_SEP);
    if (parts.length < 2) return null; // not an RV doc id; do not guess
    const [groupId, ...rest] = parts;
    return `rental_verification:${groupId}#${rest.join(DOC_ID_SEP)}`;
  }

  // Every other kind is addressed by a single native id: the signature
  // family's groupId, an intake group, an owner group, a W-9 version id.
  return `${itemKind}:${docId}`;
}
